// RBAC stage b (2026-07-17): the caller's effective permission set, fetched
// once per signed-in person from /users/me/permissions. Admin gets ['*'].
//
// ⚠️ ONE PERSON'S SET, NEVER THE NEXT ONE'S (2026-10-01). The cache used to
// live for the page's lifetime and nothing ever cleared it: after an admin
// signed out, the next person to sign in on that tab inherited '*' in every
// component that was already mounted — the app-level notification host
// subscribed a designer to the order feed. The set is now tied to the user id
// it was loaded for, and wiped on sign-out (lib/signOutCleanup.ts).
import { useEffect, useState } from 'react';
import { usersApi } from '@/lib/api';
import { useAuthStore } from '@/store/useAuthStore';
import { onSignedOut } from './signOutCleanup';

let cachedFor: string | null = null;
let cached: Set<string> | null = null;
let inflight: Promise<Set<string>> | null = null;

async function load(userId: string): Promise<Set<string>> {
  if (cachedFor !== userId) { cachedFor = userId; cached = null; inflight = null; }
  if (cached) return cached;
  if (!inflight) {
    inflight = usersApi.mePermissions()
      .then((r) => {
        const perms = new Set(r.permissions ?? []);
        if (cachedFor === userId) cached = perms;
        return perms;
      })
      .catch(() => new Set<string>());
  }
  return inflight;
}

export function clearPermissionCache(): void { cachedFor = null; cached = null; inflight = null; }
onSignedOut(clearPermissionCache);

/** Hook: null while loading (or signed out), then the set. can('*') users pass everything. */
export function usePermissions(): { perms: Set<string> | null; can: (p: string) => boolean } {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const [loaded, setLoaded] = useState<{ userId: string; perms: Set<string> } | null>(
    () => (userId && cachedFor === userId && cached ? { userId, perms: cached } : null),
  );
  // ALWAYS await load(), even when the cache looks populated at effect time.
  // The old `if (!cached)` guard had a race: a component that mounted while the
  // cache was empty (so useState captured null) but whose effect ran after
  // another component had filled it would skip loading entirely — and, having no
  // other reason to re-render, would keep `perms === null` and treat every
  // can() as false forever. That is exactly how the app-level notification toast
  // silently never subscribed while the header bell worked.
  // Re-runs when the signed-in person changes, so a set is never carried over.
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    load(userId).then((perms) => {
      if (!alive) return;
      setLoaded((prev) => (prev?.userId === userId && prev.perms === perms ? prev : { userId, perms }));
    });
    return () => { alive = false; };
  }, [userId]);
  const effective = !userId
    ? null
    : loaded?.userId === userId
      ? loaded.perms
      : cachedFor === userId ? cached : null;
  return {
    perms: effective,
    can: (p: string) => !!effective && (effective.has('*') || effective.has(p)),
  };
}
